import moment from 'moment';

import formatCycleRuntime from '@shared/lib/formatter/formatCycleRuntime';
import convertToCelsius from '@shared/lib/converter/convertToCelsius';
import convertToCelsiusResult from '@shared/lib/converter/convertToCelsiusResult';
import convertToDayMonth from '@shared/lib/converter/convertToDayMonth';
import actions from './leadDetailAction';
import mutations from './leadDetailMutation';
import initialState from './leadDetailInitialState';

const getters = {
  leadCycleData: (state, getter, rootState) => {
    const {
      admin: {
        partnerSettings: {
          TEMP_UNIT = '°F',
          PRESSURE_UNIT = 'inH2O',
          DATE_FORMAT = 'MM-DD-YYYY',
        },
      },
    } = rootState;
    const {
      cycles,
      pressureConversion,
    } = state;
    const reversedCycles = cycles.slice().reverse();
    const ctd = reversedCycles.slice(0, 6).map((c) => {
      const {
        acMode,
        tempAPI,
        rhAPI,
        supplyAir,
        returnAir,
        deltaT,
        pressureChange,
        startTime,
        cycleTimeSeconds,
        dtPayload,
      } = c;

      let deltaP = 0;
      if (pressureChange && pressureChange.onChange) {
        if (PRESSURE_UNIT === 'inH2O') {
          deltaP = `${Math.abs(pressureChange.onChange / pressureConversion).toFixed(2)}inH2O`;
        } else {
          deltaP = `${pressureChange.onChange}Pa`;
        }
      }

      let rh;
      if (!rhAPI) {
        rh = '--';
      } else {
        rh = rhAPI;
      }

      let oaTemp = tempAPI;
      let performance = deltaT;
      let supplyAirTemp;
      let returnAirTemp;
      if (dtPayload) {
        supplyAirTemp = dtPayload.sup;
        returnAirTemp = dtPayload.ret;
      } else {
        supplyAirTemp = acMode === 'Cool' ? supplyAir.minTemp : supplyAir.maxTemp;
        returnAirTemp = acMode === 'Cool' ? returnAir.minTemp : returnAir.maxTemp;
      }
      if (TEMP_UNIT === '°C') {
        oaTemp = convertToCelsius(tempAPI);
        performance = convertToCelsiusResult(deltaT);
        supplyAirTemp = convertToCelsius(supplyAirTemp);
        returnAirTemp = convertToCelsius(returnAirTemp);
      }
      let date = moment(startTime).format('M-D-YYYY');
      if (DATE_FORMAT === 'DD-MM-YYYY') {
        date = convertToDayMonth(startTime);
      }

      const cd = {
        oaTemp: `${oaTemp}${TEMP_UNIT}/${rh}`,
        deltaP,
        time: moment(startTime).format('h:mmA'),
        runtime: formatCycleRuntime(cycleTimeSeconds),
        date,
        supRet: `${(supplyAirTemp).toFixed(0)}${TEMP_UNIT}/${(returnAirTemp).toFixed(0)}${TEMP_UNIT}`,
      };

      if (acMode === 'Cool') {
        cd.performance = `-${performance.toFixed(0)}${TEMP_UNIT}`;
      } else if (acMode === 'Heat') {
        cd.performance = `+${performance.toFixed(0)}${TEMP_UNIT}`;
      }
      return cd;
    });
    return ctd.slice(0, 5);
  },
  sensorGraphData: (state, getter, rootState) => {
    const {
      admin: {
        partnerSettings: {
          TEMP_UNIT = '°F',
          DATE_FORMAT = 'MM-DD-YYYY',
        },
      },
    } = rootState;
    const {
      cycles,
    } = state;
    const supplyTemp = [];
    const returnTemp = [];
    const deltaTemp = [];
    const cycleDate = [];
    cycles.forEach((c) => {
      const {
        supplyAir,
        returnAir,
        acMode,
        deltaT,
        startTime,
      } = c;
      let supplyAirTemp = acMode === 'Heat' ? supplyAir?.maxTemp : supplyAir?.minTemp;
      let returnAirTemp = acMode === 'Heat' ? returnAir?.maxTemp : returnAir?.minTemp;
      let performance = deltaT;
      if (TEMP_UNIT === '°C') {
        supplyAirTemp = convertToCelsius(supplyAirTemp);
        returnAirTemp = convertToCelsius(returnAirTemp);
        performance = convertToCelsiusResult(performance);
      }
      if (supplyAirTemp) {
        const temp = Math.round(supplyAirTemp);
        supplyTemp.unshift(temp);
      } else {
        supplyTemp.unshift(null);
      }
      if (returnAirTemp) {
        const temp = Math.round(returnAirTemp);
        returnAirTemp.unshift(temp);
      } else {
        returnAirTemp.unshift(null);
      }
      if (performance) {
        const temp = Math.abs(performance);
        deltaTemp.unshift(temp);
      } else {
        deltaTemp.unshift(null);
      }
      let date = moment(startTime).format('M-D-YYYY');
      if (DATE_FORMAT === 'DD-MM-YYYY') {
        date = convertToDayMonth(startTime);
      }
      cycleDate.unshift(date);
    });
    return {
      supplyTemp,
      returnTemp,
      deltaTemp,
      cycleDate,
    };
  },
  gaugeGraphData: (state, getter, rootState) => {
    const {
      admin: {
        partnerSettings: {
          TEMP_UNIT = '°F',
          PRESSURE_UNIT = 'inH20',
          DATE_FORMAT = 'MM-DD-YYYY',
        },
      },
    } = rootState;
    const {
      lead,
    } = state;
    if (lead.alerts.length > 0) {
      let temp = lead.alerts[0].boundaryValue;
      if (TEMP_UNIT === '°C') {
        temp = convertToCelsiusResult(temp);
      }
      return {
        temp,
        graphType: lead.alerts[0].type,
      };
    }
    return {
      temp: 0,
      graphType: null,
    };
  },
  runtimeBarGraphData: (state) => {
    const {
      lead,
    } = state;
    let runtime = 0;
    let totalRuntime = 0;
    if (lead.alerts.length > 0) {
      runtime = Math.abs(lead.alerts[0].boundaryValue * 3600);
      totalRuntime = formatCycleRuntime(runtime);
    }
    return {
      runtime,
      totalRuntime,
    };
  },
  runtimeLineGraphData: (state, getter, rootState) => {
    const {
      admin: {
        partnerSettings: {
          TEMP_UNIT = '°F',
          DATE_FORMAT = 'MM-DD-YYYY',
        },
      },
    } = rootState;
    const {
      cycles,
    } = state;
    const runtimes = [];
    const oaTemp = [];
    const cycleDate = [];
    cycles.forEach((c) => {
      const {
        startTime,
        cycleTimeSeconds,
        tempAPI,
      } = c;
      if (cycleTimeSeconds) {
        runtimes.push(cycleTimeSeconds);
      } else {
        runtimes.push(null);
      }
      if (tempAPI) {
        let temp = tempAPI;
        if (TEMP_UNIT === '°C') {
          temp = convertToCelsius(tempAPI);
        }
        oaTemp.push(temp);
      } else {
        oaTemp.push(null);
      }
      if (startTime) {
        let date = moment(startTime).format('M-D-YYYY');
        if (DATE_FORMAT === 'DD-MM-YYYY') {
          date = convertToDayMonth(startTime);
        }
        cycleDate.unshift(date);
      } else {
        cycleDate.push(null);
      }
    });
    return {
      runtimes,
      oaTemp,
      cycleDate,
    };
  },
  pastAlertsData: (state, getter, rootState) => {
    const {
      admin: {
        partnerSettings: {
          DATE_FORMAT = 'MM-DD-YYYY',
        },
      },
    } = rootState;
    const {
      pastAlerts,
    } = state;
    const leakAlertList = [];
    const restAlertList = [];
    pastAlerts.forEach((pa) => {
      let date = moment(pa.startTime).format('M-D-YYYY');
      let endDate = moment(pa.lastOccurred).format('M-D-YYYY');
      if (DATE_FORMAT === 'DD-MM-YYYY') {
        date = convertToDayMonth(pa.startTime);
        endDate = convertToDayMonth(pa.lastOccurred);
      }
      if (pa.type === 'Water Detected') {
        leakAlertList.push({
          type: pa.type,
          title: pa.title,
          location: pa.location,
          startDate: date,
          endDate,
        });
      } else {
        restAlertList.push({
          type: pa.type,
          title: pa.title,
          occurrence: pa.occurrence,
          startDate: date,
        });
      }
    });
    return {
      leakAlertList,
      restAlertList,
    };
  },
  otherAlertsData: (state, getter, rootState) => {
    const {
      admin: {
        partnerSettings: {
          DATE_FORMAT = 'MM-DD-YYYY',
        },
      },
    } = rootState;
    const {
      pastAlerts,
    } = state;

    const alerts = [];
    pastAlerts.forEach((a) => {
      let startTime = moment(a.startTime).format('M-D-YYYY');
      let endTime = moment(a.lastOccurred).format('M-D-YYYY');
      if (DATE_FORMAT === 'DD-MM-YYYY') {
        startTime = convertToDayMonth(a.startTime);
        endTime = convertToDayMonth(a.lastOccurred);
      }
      if (a.type !== 'Water Detected') {
        alerts.push({
          type: a.type,
          status: a.severity,
          zone: a.systemName,
          startTime,
          endTime,
          realData: true,
        });
      }
    });

    if (alerts.length < 5) {
      const index = 5 - alerts.length;
      for (let i = 1; i <= index; i += 1) {
        alerts.push({
          type: '',
          occurrence: '',
          startTime: '',
        });
      }
    }
    return alerts;
  },
};

const eventModule = {
  state: initialState(),
  mutations,
  actions,
  getters,
};

export default eventModule;
